import type { IAuthor } from '@/types/author'
import type {
  IStructuredDataArticle,
  IStructuredDataPage,
  IStructuredDataProduct,
} from '@/types/seo'

import { type ClassValue, clsx } from 'clsx'
import dayjs from 'dayjs'
import { twMerge } from 'tailwind-merge'
import 'dayjs/locale/pt-br'

dayjs.locale('pt-br')

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export enum DATE_FORMATS {
  DATE = 'DD/MM/YYYY',
  DATE_LOCAL = 'DD [de] MMMM [de] YYYY',
  DATE_TIME = 'DD/MM/YYYY HH:mm',
  TIME = 'HH:mm',
  YEAR_MONTH_DAY = 'YYYY-MM-DD',
}

export function encrypt(text: string) {
  const CRYPTO_SHIFT = 10
  return encodeURIComponent(
    text
      .split('')
      .map((char) => {
        const code = char.charCodeAt(0)
        return String.fromCharCode(code + CRYPTO_SHIFT)
      })
      .join(''),
  )
}

export function decrypt(text: string) {
  const CRYPTO_SHIFT = 10
  return decodeURIComponent(text)
    .split('')
    .map((char) => {
      const code = char.charCodeAt(0)
      return String.fromCharCode(code - CRYPTO_SHIFT)
    })
    .join('')
}

export const copyToClipboard = (text: string): void => {
  navigator.clipboard.writeText(text)
}

export const buildAuthor = (author: {
  node: {
    name: string
    slug: string
    description: string
    avatar: { url: string }
  }
}): IAuthor => {
  return {
    name: author?.node?.name,
    slug: author?.node?.slug,
    description: author?.node?.description || '',
    avatarUrl: author?.node?.avatar?.url
      ? author?.node?.avatar?.url
      : `https://ui-avatars.com/api/?name=${author?.node?.name}&=rounded=true&background=C4C4C4&color=1A1A1A`,
  }
}

export function dateFormat(date: Date | string, format: DATE_FORMATS) {
  if (!date) return ''
  return dayjs(date).format(format)
}

export const buildDate = (date: Date | string) => {
  return {
    _d: new Date(date),
    d: dateFormat(date, DATE_FORMATS.DATE),
    dl: dateFormat(date, DATE_FORMATS.DATE_LOCAL),
    dtl: dateFormat(date, DATE_FORMATS.DATE_TIME),
    t: dateFormat(date, DATE_FORMATS.TIME),
    h: calculateTimePassed(date),
  }
}

export const buildContent = ({
  excerpt,
  content,
}: {
  excerpt?: string
  content?: string
}) => {
  const result:
    | {
        text: { html: string; plain: string } | null
        content: { html: string; plain: string } | null
      }
    | { text: null; content: null } = { text: null, content: null }

  if (excerpt) {
    result.text = {
      html: excerpt,
      plain: removeHtmlTags(excerpt),
    }
  }

  if (content) {
    result.content = {
      html: content?.replaceAll(
        'http://cms.direcaoconcursos',
        'https://wordpress.direcaoconcursos',
      ),
      plain: removeHtmlTags(content),
    }
  }

  return result
}

export const toUTC = (date: Date | string): string => {
  const saoPauloOffset = -3

  return dayjs(date).subtract(saoPauloOffset, 'hour').toISOString()
}

export const calculateTimePassed = (date: Date | string): string => {
  const now = dayjs()
  const past = dayjs(toUTC(date))

  const minutes = now.diff(past, 'minute')
  const hours = now.diff(past, 'hour')
  const days = now.diff(past, 'day')
  const weeks = now.diff(past, 'week')
  const months = now.diff(past, 'month')
  const years = now.diff(past, 'year')

  if (years > 1) return `há ${years} anos`
  if (years === 1) return 'ano passado'
  if (months > 1) return `há ${months} meses`
  if (months === 1) return 'mês passado'
  if (weeks > 1) return `há ${weeks} semanas`
  if (weeks === 1) return 'semana passada'
  if (days > 1) return `há ${days} dias`
  if (days === 1) return 'ontem'
  if (hours > 1) return `há ${hours} horas`
  if (hours === 1) return 'há 1 hora'
  if (minutes > 1) return `há ${minutes} minutos`

  return 'agora mesmo'
}

export const removeHtmlTags = (str: string): string => {
  const entities: { [key: string]: string } = {
    '&nbsp;': ' ',
    '&amp;': '&',
    '&quot;': '"',
    '&#39;': "'",
    '&lt;': '<',
    '&gt;': '>',
    '&hellip;': '...',
    '[&hellip;]': '...',
    '&ldquo;': '“',
    '&rdquo;': '”',
    '&lsquo;': '‘',
    '&rsquo;': '’',
  }

  return str
    ?.replace(/<[^>]*>/g, '')
    ?.replace(
      /&[a-zA-Z0-9#]+;|(\[?&[a-zA-Z0-9#]+;]?\])/g,
      (match) => entities[match] || match,
    )
}

export const removeStyleTags = (str: string): string => {
  return str?.replace(/style="[^"]*"/g, '')
}

export const removeHeadingTags = (str: string): string => {
  return str
    ?.replace(/<h1>(.*?)<\/h1>/gi, '<p class="text-xl">$1</p>')
    ?.replace(/<h2>(.*?)<\/h2>/gi, '<p class="text-lg">$1</p>')
    ?.replace(/<h3>(.*?)<\/h3>/gi, '<p class="text-md">$1</p>')
}

export const getInstallmentValue = (
  maxInstallments: number,
  minValue: number,
  price: number,
  custom: { amount: number; value: number },
) => {
  if (!price) return null

  const recommendation = new Array(maxInstallments)
    .fill(1)
    .map((_, amount) => ({
      amount: amount + 1,
      value: price / (amount + 1),
      valid: price / (amount + 1) >= minValue,
    }))
    .reverse()
    .find((t) => t.valid)

  return recommendation?.amount && recommendation?.amount < custom.amount
    ? recommendation
    : custom
}

export type TMask =
  | string
  | 'phone'
  | 'credit_card'
  | 'cpf'
  | 'cep'
  | 'birthdate'

export const mask = (value: string | null | undefined, type: TMask): string => {
  if (!value) return ''

  switch (type) {
    case 'phone':
      return value
        .replace(/\D/g, '')
        .replace(/^(\d{2})(\d)/g, '($1) $2')
        .replace(/(\d{4})(\d)/, '$1-$2')
        .replace(/(\d{4})-(\d)(\d{4})/, '$1$2-$3')
        .replace(/(-\d{4})\d+?$/, '$1')

    case 'card_number':
      return value
        .replace(/\D/g, '')
        .replace(/(\d{4})(\d)/, '$1 $2')
        .replace(/(\d{4})(\d)/, '$1 $2')
        .replace(/(\d{4})(\d)/, '$1 $2')
        .replace(/(\d{4})(\d)/, '$1')

    case 'card_date':
      return value
        .replace(/\D/g, '')
        .replace(/(\d{2})(\d)/, '$1/$2')
        .replace(/(\d{2})(\d)/, '$1')

    case 'card_name':
      return value
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-zA-Z0-9 ]/g, '')

    case 'cpf':
      return value
        .replace(/\D/g, '')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d{1,2})/, '$1-$2')
        .replace(/(-\d{2})\d+?$/, '$1')

    case 'cnpj':
      return value
        .replace(/\D/g, '')
        .replace(/(\d{2})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1.$2')
        .replace(/(\d{3})(\d)/, '$1/$2')
        .replace(/(\d{4})(\d{1,2})/, '$1-$2')
        .replace(/(-\d{2})\d+?$/, '$1')

    case 'cep':
      return value
        .replace(/\D/g, '')
        .replace(/(\d{8})\d+?$/, '$1')
        .replace(/^(\d{5})(\d{3})$/, '$1-$2')
        .replace(/^(\d{5})(\d{1,2})$/, '$1-$2')

    case 'birthdate':
      return value
        .replace(/\D/g, '')
        .replace(/(\d{2})(\d)/, '$1/$2')
        .replace(/(\d{2})(\d)/, '$1/$2')
        .replace(/(\d{4})(\d)/, '$1')

    default:
      return value
  }
}

export function individualDocumentValidate(document: string): boolean {
  const doc = document.replace(/[^\d]+/g, '')
  if (doc === '') return false

  // CPF validation
  if (doc.length === 11) {
    if (
      doc === '00000000000' ||
      doc === '11111111111' ||
      doc === '22222222222' ||
      doc === '33333333333' ||
      doc === '44444444444' ||
      doc === '55555555555' ||
      doc === '66666666666' ||
      doc === '77777777777' ||
      doc === '88888888888' ||
      doc === '99999999999'
    )
      return false

    let add = 0
    for (let i = 0; i < 9; i++) add += Number.parseInt(doc.charAt(i)) * (10 - i)
    let rev = 11 - (add % 11)
    if (rev === 10 || rev === 11) rev = 0
    if (rev !== Number.parseInt(doc.charAt(9))) return false

    add = 0
    for (let i = 0; i < 10; i++)
      add += Number.parseInt(doc.charAt(i)) * (11 - i)
    rev = 11 - (add % 11)
    if (rev === 10 || rev === 11) rev = 0
    if (rev !== Number.parseInt(doc.charAt(10))) return false
    return true
  }

  return false
}

export function companyDocumentValidate(document: string): boolean {
  const doc = document.replace(/[^\d]+/g, '')
  if (doc === '') return false

  // CNPJ validation
  if (doc.length === 14) {
    if (
      doc === '00000000000000' ||
      doc === '11111111111111' ||
      doc === '22222222222222' ||
      doc === '33333333333333' ||
      doc === '44444444444444' ||
      doc === '55555555555555' ||
      doc === '66666666666666' ||
      doc === '77777777777777' ||
      doc === '88888888888888' ||
      doc === '99999999999999'
    )
      return false

    let size = doc.length - 2
    let numbers = doc.substring(0, size)
    const digits = doc.substring(size)
    let sum = 0
    let pos = size - 7

    for (let i = size; i >= 1; i--) {
      sum += Number.parseInt(numbers.charAt(size - i)) * pos--
      if (pos < 2) pos = 9
    }

    let result = sum % 11 < 2 ? 0 : 11 - (sum % 11)
    if (result !== Number.parseInt(digits.charAt(0))) return false

    size += 1
    numbers = doc.substring(0, size)
    sum = 0
    pos = size - 7

    for (let i = size; i >= 1; i--) {
      sum += Number.parseInt(numbers.charAt(size - i)) * pos--
      if (pos < 2) pos = 9
    }

    result = sum % 11 < 2 ? 0 : 11 - (sum % 11)
    if (result !== Number.parseInt(digits.charAt(1))) return false

    return true
  }

  return false
}

export function birthdateValidate(value: string): boolean {
  // Check format DD/MM/YYYY
  if (!/^\d{2}\/\d{2}\/\d{4}$/.test(value)) return false

  const [day, month, year] = value.split('/').map(Number)
  const date = new Date(year, month - 1, day)

  // Check if valid date (handles cases like 31/02/2024)
  if (
    date.getDate() !== day ||
    date.getMonth() !== month - 1 ||
    date.getFullYear() !== year
  ) {
    return false
  }

  const now = new Date()
  const age =
    now.getFullYear() -
    year -
    (now.getMonth() < month - 1 ||
    (now.getMonth() === month - 1 && now.getDate() < day)
      ? 1
      : 0)

  // Check if date is not in future and age is reasonable (0-120 years)
  return date <= now && age >= 18 && age <= 120
}

export function getInitials(value: string, slice?: number): string {
  return value
    .split(' ')
    .slice(0, slice || 2)
    .map((word) => word[0])
    .join('')
}

export function getPrice(price: number): string {
  return price?.toLocaleString('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    currency: 'BRL',
    style: 'currency',
  })
}

export function workloadFormat({
  totalExcerpts,
  totalVideos,
}: {
  totalExcerpts: number
  totalVideos: number
}): string {
  const meanReading = 1798 //mean reading time in seconds
  const meanWatching = 1756 //mean video time in seconds

  const text = totalExcerpts ? totalExcerpts * meanReading : 0
  const video = totalVideos ? totalVideos * meanWatching : 0

  const total = text + video
  let workloadDays = total > 0 ? Math.round(total / 60 / 60 / 24) : 1

  let workloadType = 'D'

  /*

    P is the duration designator (for period) placed at the start of the duration representation.
    Y is the year designator that follows the value for the number of calendar years.
    M is the month designator that follows the value for the number of calendar months.
    W is the week designator that follows the value for the number of weeks.
    D is the day designator that follows the value for the number of calendar days.

    T is the time designator that precedes the time components of the representation.
    H is the hour designator that follows the value for the number of hours.
    M is the minute designator that follows the value for the number of minutes.
    S is the second designator that follows the value for the number of seconds.

  */

  if (workloadDays < 30) {
    workloadType = 'D'
  } else if (workloadDays > 30) {
    workloadType = 'M'
    workloadDays = Math.round(workloadDays / 30)
  } else if (workloadDays > 548) {
    workloadType = 'A'
    workloadDays = Math.round(workloadDays / 365)
  }

  return `P${workloadDays}${workloadType}`
}

export function getDescription(
  description: string,
  name: string,
  type: 'course' | 'combo' | 'module',
) {
  if (
    description &&
    description
      ?.replace(/<[^>]*>/g, '')
      ?.replace(/&[^;]+;/g, '')
      ?.trim()?.length > 0
  )
    return removeHtmlTags(description)

  if (type === 'course')
    return `Aprimore suas habilidades e conhecimentos com o curso ${name} do Direção Concursos. Neste curso abrangente, você terá acesso a um conteúdo de alta qualidade ministrado por especialistas na área. Aprofunde-se nos tópicos mais relevantes e atualizados, garantindo uma preparação completa e eficiente para os concursos mais exigentes. Invista no seu futuro com o curso ${name} e esteja pronto para alcançar excelentes resultados em suas provas`

  if (type === 'combo')
    return `Domine todo o conteúdo com o Combo ${name} do Direção Concursos. Aprenda com os melhores módulos e cursos preparatórios que abrangem todas as disciplinas essenciais para o seu sucesso nos concursos. Alcance o conhecimento completo e conquiste a tão sonhada aprovação. Potencialize seu desempenho com o Combo ${name} e esteja preparado para qualquer desafio.`

  if (type === 'module')
    return `Aprofunde seus estudos com o Módulo ${name} da Direção Concursos. Neste módulo, você encontrará um conteúdo especializado e direcionado para o concurso que deseja enfrentar. Desenvolvido por nossa equipe de especialistas, o Módulo ${name} aborda os tópicos mais relevantes de forma aprofundada, permitindo que você se prepare de maneira sólida e completa. Através deste recurso, você poderá revisar os principais assuntos e resolver questões para consolidar seu aprendizado. Potencialize seus conhecimentos com o Módulo ${name} e esteja preparado para superar os desafios do concurso com confiança e sucesso.`

  return ''
}

export function getStructuredData(
  schema: IStructuredDataArticle | IStructuredDataProduct | IStructuredDataPage,
) {
  if (schema?.type === 'Course') {
    const course_schema = schema as IStructuredDataProduct

    const children = course_schema.children?.length
      ? course_schema.children?.map(
          (course: { name: string; permanentLink: string }, index: number) => {
            return {
              '@type': 'ListItem',
              position: index + 1,
              name: course.name,
              url: course?.permanentLink || '',
            }
          },
        )
      : []

    const teachers = course_schema.teachers.map(
      (
        teacher: { slug: string; name: string; image: string },
        index: number,
      ) => {
        return {
          '@type': 'ListItem',
          url: `/professores/${teacher.slug}`,
          position: index + 1,
          item: {
            '@type': 'Thing',
            alternateName: teacher.name,
            image: teacher.image,
            url: `/professores/${teacher.slug}` || '',
          },
        }
      },
    )

    return {
      name: course_schema.title,
      description: course_schema.description,
      headline: course_schema.title,
      hasCourseInstance: {
        '@type': 'CourseInstance',
        courseMode: 'online',
        courseWorkload: course_schema.workload,
      },
      offers: {
        '@type': 'Offer',
        price: course_schema.offer,
        priceCurrency: 'BRL',
        name: course_schema.title,
        url: course_schema.url,
        category: course_schema.category,
        seller: {
          '@type': 'Organization',
          name: 'Direção Concursos',
        },
      },
      about: {
        '@type': 'CreativeWork',
        accessModeSufficient: {
          '@type': 'ItemList',
          numberOfItems: teachers.length,
          itemListElement: teachers,
        },
      },
      subjectOf: {
        '@type': 'CreativeWork',
        accessModeSufficient: {
          '@type': 'ItemList',
          numberOfItems: children.length,
          itemListElement: children,
        },
      },
    }
  }

  if (['Article', 'NewsArticle'].includes(schema?.type)) {
    const article_schema = schema as IStructuredDataArticle
    return {
      name: article_schema.title,
      headline: article_schema.title,
      description: article_schema.description?.replace(/<[^>]+>/g, ''),
      author: {
        '@type': 'Person',
        name: article_schema.authorName || 'Direção Concursos',
        url: article_schema.authorUrl,
      },
      datePublished: article_schema.datePublished,
      dateModified: article_schema.dateModified,
      image: article_schema.image,
      articleSection:
        article_schema.articleSection ||
        article_schema.description?.replace(/<[^>]+>/g, ''),
      articleBody:
        article_schema.articleBody ||
        article_schema.description?.replace(/<[^>]+>/g, ''),
      url: article_schema?.url?.replace(
        'https://wordpress.direcaoconcursos',
        'https://www.direcaoconcursos',
      ),
      mainEntityOfPage: article_schema?.url,
      keywords: article_schema?.keywords,
    }
  }

  if (schema?.type === 'WebPage') {
    const page_schema = schema as IStructuredDataPage
    return {
      name: page_schema.title,
      headline: page_schema.title,
      mainEntity: page_schema.mainEntity,
      url: page_schema.url,
    }
  }
}

export function blurEmail(email?: string) {
  if (!email) return email
  const [nome, dominio] = email.split('@')
  const nameMasked = nome
    .substring(0, Math.min(4, nome.length))
    .padEnd(nome.length, '●')
  const domainParsed = dominio.split('.')
  const domainMasked = domainParsed
    .map((parte, index) =>
      index === 0
        ? parte
            .substring(0, Math.min(4, parte.length))
            .padEnd(parte.length, '●')
        : parte,
    )
    .join('.')
  return `${nameMasked}@${domainMasked}`
}

export function blurDocument(document?: string) {
  if (!document) return document

  if (document.length === 14) {
    const [_0, _1, _2, afterDash] = document.split(/\.|-/)
    const beforeDotMask = '●'.repeat(3)
    const betweenDotMask = '●'.repeat(3)
    const afterDashMask = afterDash.padStart(2, '●')
    return `${beforeDotMask}.${betweenDotMask}.${betweenDotMask}-${afterDashMask}`
  }

  if (document.length > 14) {
    const [_0, _1, _2, _afterSlash, afterDash] = document.split(/\.|-|\//)
    const betweenDotMask = '●'.repeat(3)
    return `${_0}.${betweenDotMask}.${betweenDotMask}/${afterDash}`
  }
}

export function getProfileFromCookie(cookie?: string) {
  if (cookie) {
    const parsed = atob(cookie)
    const [_id, email] = parsed.split('-')
    return { _id, email }
  }
  return {}
}

export function validateCardDate(value: string) {
  if (!value) return false
  const cardDate = value.split('/')
  const thisMonth = new Date().getMonth() + 1
  const thisYear = new Date().getFullYear() % 100
  if (Number(cardDate[1]) < Number(thisYear)) return false
  if (Number(cardDate[0]) > 12) return false
  if (
    Number(cardDate[0]) < thisMonth &&
    Number(cardDate[1]) === Number(thisYear)
  )
    return false

  return true
}

export function getProductType(type: string) {
  return type === 'subscription'
    ? 'Assinatura'
    : type === 'course'
      ? 'Curso'
      : type === 'combo'
        ? 'Combo'
        : 'Módulo'
}

export function getYoutubeEmbed(url: string) {
  const regex =
    /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e|embed|watch|shorts|live)\/|.*[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})|(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:live|v|e|embed|watch|shorts)\/|.*[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
  const match = url.match(regex)
  const id = match ? match[1] || match[2] : null
  return `https://www.youtube.com/embed/${id}`
}

export function getYoutubeThumbnail<IImageResolution>(url: string) {
  const regex =
    /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e|embed|watch|shorts|live)\/|.*[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})|(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:live|v|e|embed|watch|shorts)\/|.*[?&]v=)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
  const match = url.match(regex)
  const id = match ? match[1] || match[2] : null

  const sizes: IImageResolution = {
    tiny: {
      url: `https://img.youtube.com/vi/${id}/default.jpg`,
      width: 120,
      height: 90,
    },
    small: {
      url: `https://img.youtube.com/vi/${id}/mqdefault.jpg`,
      width: 320,
      height: 180,
    },
    medium: {
      url: `https://img.youtube.com/vi/${id}/hqdefault.jpg`,
      width: 480,
      height: 360,
    },
    large: {
      url: `https://img.youtube.com/vi/${id}/sddefault.jpg`,
      width: 640,
      height: 480,
    },
    xlarge: {
      url: `https://img.youtube.com/vi/${id}/maxresdefault.jpg`,
      width: 1920,
      height: 1080,
    },
  } as IImageResolution
  return sizes
}

//import { navigate } from 'astro:transitions/client'
export const navigate = (url: string) => {
  window.location.href = url
}

export const open = (url: string) => {
  window.open(url, '_blank', 'noopener,noreferrer')
}

export const logger = (id: string, message: string) => {
  console.error(`Error: ECOMMERCE LOGGER ${id}`, message)
  console.trace(`ECOMMERCE LOGGER ${id}`)
}

export const roundTo = (value: number, places: number) => {
  return !Number.isNaN(Number(value)) ? +Number(value).toFixed(places) : value
}

export function getQueryParams(key: string): string | null {
  const e = location.search
    .slice(1)
    .split('&')
    .map((e) => e.split('='))
    .reduce(
      (e, [n, t]) => ({
        // biome-ignore lint/performance/noAccumulatingSpread: <explanation>
        ...e,
        [n]: t,
      }),
      {},
    )
  return Object.keys(e).length > 0 ? e[key] : null
}
